import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../services/api/auth.service';
import { environment } from '../../../environments/environment';
import { LocalStorageService } from '../services/storage/local-storage.service';
import { catchError, switchMap, throwError } from 'rxjs';

// Variable para evitar bucles infinitos de refresh
let isRefreshing = false;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const storage = inject(LocalStorageService);
    const injector = inject(Injector);
    const router = inject(Router);

    // Lista de rutas que NO requieren autenticación
    const publicEndpoints = [
        '/auth/login', 
        '/auth/register', 
        '/auth/forgot-password', 
        '/auth/restore-password', 
        '/auth/reset-password', 
        '/api/v1/rate-driver',
        '/api/v1/version',
        '/api/v1/health'
    ];

    // Lista de rutas que NO deben intentar refresh token
    const noRefreshEndpoints = [
        '/auth/refresh',
        '/auth/logout'
    ];

    // Verificar si es un endpoint público
    const isPublicEndpoint = publicEndpoints.some(endpoint => req.url.includes(endpoint));

    // Si es un endpoint público, solo añadir headers básicos
    if (isPublicEndpoint) {
        req = req.clone({
            setHeaders: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            }
        });
        return next(req);
    }

    // Solo agregar headers para requests a la API
    if (req.url.startsWith(environment.apiUrl)) {
        const headers: { [key: string]: string } = {
            Accept: 'application/json'
        };

        // Solo agregar Content-Type si no está ya presente
        if (!(req.body instanceof FormData)) {
            if (!req.headers.has('Content-Type')) {
                headers['Content-Type'] = 'application/json';
            }
        }

        // Agregar token de autorización si existe
        const token = storage.getItem('token');
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        req = req.clone({
            setHeaders: headers
        });
    }

    // Manejar la respuesta y errores
    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            // Verificar si es un error 401 y si debemos intentar refresh
            const shouldTryRefresh = error.status === 401
                && !isRefreshing
                && !noRefreshEndpoints.some(endpoint => req.url.includes(endpoint));

            if (shouldTryRefresh) {
                isRefreshing = true;
                const authService = injector.get(AuthService);

                return authService.refreshToken().pipe(
                    switchMap((response: any) => {
                        isRefreshing = false;
                        const newToken = response?.data?.token || response?.token;

                        if (newToken) {
                            // Guardar el nuevo token
                            storage.setItem('token', newToken);

                            // Clonar la request con el nuevo token
                            const newReq = req.clone({
                                setHeaders: {
                                    ...req.headers.keys().reduce(
                                        (acc, key) => {
                                            acc[key] = req.headers.get(key) || '';
                                            return acc;
                                        },
                                        {} as { [key: string]: string }
                                    ),
                                    Authorization: `Bearer ${newToken}`
                                }
                            });

                            // Reintentar la petición con el nuevo token
                            return next(newReq);
                        }

                        // Si no hay token, limpiar y redirigir
                        storage.removeItem('token');
                        router.navigate(['/auth/login']);
                        return throwError(() => error);
                    }),
                    catchError((err) => {
                        isRefreshing = false;
                        // Si falla el refresh, limpiar todo y redirigir
                        storage.removeItem('token');
                        router.navigate(['/auth/login']);
                        return throwError(() => err);
                    })
                );
            }

            // Si es un error 401 en logout o refresh-token, solo limpiar y redirigir
            if (error.status === 401 && noRefreshEndpoints.some(endpoint => req.url.includes(endpoint))) {
                storage.removeItem('token');
                router.navigate(['/auth/login']);
            }

            return throwError(() => error);
        })
    );
};
